import React, { Component, useCallback, useRef, useState } from "reactn";
import styled from "styled-components";
import moment from "moment";
import classNames from "classnames";

import {
    Avatar,
    Icon,
    Tooltip,
    Menu,
    MenuItem
} from "@material-ui/core";
import { MuiThemeProvider } from "@material-ui/core/styles";

import muiTheme from "js/react/materialThemeOverrides";
import { ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";

import { MAX_COMMENT_REPLIES } from "../../../../common/constants";

import CommentComposer, { EditContentEditable } from "./CommentComposer";
import { getLikeTooltipTitle } from "./commentUtils";

const Container = styled.div`
    position: relative;
    transition: "opacity 500ms";
`;

const PostedCard = styled.div`
    position: relative;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #E4E4E4;
    padding: 12px;
    margin-bottom: 10px;

    .viewer & {
        background: #444;
        border-color: transparent;
    }
`;

const CardTop = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 10px;
`;

const PostedAvatar = styled(Avatar)`
    &&& {
        width: 30px;
        height: 30px;
        color: #ccc;
    }
`;

const HeaderText = styled.div`
    flex: 1;
    min-width: 0;
`;

const AuthorName = styled.div`
    font-weight: 600;
    font-size: 13px;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    .editor & {
        color: #222;
    }
    .viewer & {
        color: #fff;
    }
`;

const SubRow = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: #A9A9A9;
    margin-top: 2px;

    .viewer & {
        color: #C4C4C4;
    }
`;

const SubDivider = styled.span`
    color: #D0D0D0;
`;

const SlideLink = styled.span`
    cursor: pointer;

    &:hover {
        text-decoration: underline;
        color: #11A9E2;
    }
`;

const TopActions = styled.div`
    display: flex;
    align-items: center;
    gap: 2px;
`;

// Copied verbatim from the team-slide "usefulness" button (inline variant) so the styling matches
// exactly; icon sized to 20px to fit the comments UI
const LikeButton = styled.button`
    appearance: none;
    border: none;
    background: #EEF3F6;
    border-radius: 999px;
    box-sizing: border-box;
    box-shadow: none;
    color: ${props => (props.$active || props.$hasCount) ? "#6F7780" : "#9AA2AA"};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: "Source Sans Pro", sans-serif;
    margin: 0;
    flex: 0 0 auto;
    gap: 3px;
    width: ${props => props.$hasCount ? "max-content" : "20px"};
    min-width: ${props => props.$hasCount ? "max-content" : "20px"};
    height: 20px;
    padding: ${props => props.$hasCount ? "0 7px 0 5px" : "0"};
    font-size: 11px;
    font-weight: 600;
    line-height: 16px;
    white-space: nowrap;
    transition: background-color 0.15s ease, color 0.15s ease;

    .MuiIcon-root {
        color: ${props => (props.$active || props.$hasCount) ? "#11A9E2" : "#9AA2AA"};
        font-size: 14px;
        margin: 0;
        padding: 0;
    }

    span {
        color: ${props => (props.$active || props.$hasCount) ? "#11A9E2" : "#9AA2AA"};
        flex: 0 0 auto;
    }

    &:hover {
        background-color: #E6EEF3;
        color: #6F7780;

        .MuiIcon-root {
            color: ${props => (props.$active || props.$hasCount) ? "#11A9E2" : "#6F7780"};
        }
    }
`;

const KebabIcon = styled(Icon)`
    &&& {
        font-size: 20px;
        cursor: pointer;
        color: #666;

        .viewer & {
            color: #bbb;
        }

        &:hover {
            color: #29b2e5;
        }
    }
`;

const CommentBodyWrap = styled.div`
    margin-top: 8px;

    .commentText {
        font-size: 13px !important;
    }
`;

const ReplyComposerWrap = styled.div`
    margin-top: 12px;

    & > * {
        margin-bottom: 0;
    }
`;

const CardDivider = styled.hr`
    border: none;
    border-top: 1px solid #E4E4E4;
    margin: 15px 0 10px;

    .viewer & {
        border-top-color: #555;
    }
`;

const ThreadDivider = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 15px 0 5px;
`;

const ThreadLine = styled.div`
    flex: 1;
    height: 1px;
    background: #E4E4E4;

    .viewer & {
        background: #555;
    }
`;

const ThreadToggle = styled.div`
    display: flex;
    align-items: center;
    gap: 1px;
    cursor: pointer;
    color: #8C8C8C;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.3px;
    text-transform: uppercase;
    user-select: none;

    .viewer & {
        color: #bbb;
    }

    &:hover {
        color: #29b2e5;
    }
`;

const ReplyBlock = styled.div`
    margin-top: 15px;

    &.withDivider {
        border-top: 1px solid #E4E4E4;
        padding-top: 15px;

        .viewer & {
            border-top-color: #555;
        }
    }
`;

const ReplyEditWrap = styled.div`
    margin-top: 15px;

    & > * {
        margin-bottom: 0;
    }
`;

const FooterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const FooterIcon = styled(Icon)`
    &&& {
        font-size: 20px;
        cursor: pointer;
        color: #8C8C8C;

        .viewer & {
            color: #bbb;
        }

        &:hover {
            color: #29b2e5;
        }

        &.disabled {
            color: #D0D0D0;
            cursor: default;

            &:hover {
                color: #D0D0D0;
            }
        }
    }
`;

/**
 * Author name that only gets a tooltip when it's actually ellipsized. The name is measured on
 * hover rather than on render because the pane can be resized and the row's available width
 * depends on siblings (like the like button growing a count) that change after mount.
 */
function AuthorNameWithTooltip({ name }) {
    const nameRef = useRef(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const measure = useCallback(() => {
        const element = nameRef.current;
        setIsTruncated(!!element && element.scrollWidth > element.clientWidth);
    }, []);

    // An empty title keeps MUI from ever opening the tooltip
    return (
        <Tooltip title={isTruncated ? name : ""} enterDelay={700} arrow>
            <AuthorName ref={nameRef} className="comment-author" onMouseEnter={measure}>
                {name}
            </AuthorName>
        </Tooltip>
    );
}

const NEW_COMMENT_PLACEHOLDER = "Post comment or @mention to notify collaborators...";
const NEW_COMMENT_PLACEHOLDER_VIEWER = "Post comment in the player...";
const REPLY_PLACEHOLDER = "Reply or @mention to notify collaborators...";
const REPLY_PLACEHOLDER_VIEWER = "Reply in the player...";

class Comment extends Component {
    constructor(props) {
        super(props);

        let text = props.text || "";
        // Migrating text to html
        if (text.includes("\n")) {
            text = text.replace(/\n/g, "<br>");
        }

        this.state = {
            text,
            kebabAnchorEl: null,
            isEditing: false,
            isReplying: false,
            repliesCollapsed: false,
            editingReplyId: null,
            replyKebabAnchorEl: null,
            activeReplyId: null
        };
    }

    componentDidMount() {
        const { isNew, markAsRead } = this.props;
        // Mark the thread read on mount if the comment or any reply is unread for me
        // (a reply can re-surface a thread as unread even after the comment was read)
        if (!isNew && this.hasUnreadForMe()) {
            markAsRead();
        }
    }

    hasUnreadForMe() {
        const { isRead, isMy, replies = [], currentUserUid } = this.props;

        if (!isMy && !isRead) {
            return true;
        }

        return replies.some(reply => reply.authorUid !== currentUserUid && !(reply.readBy && reply.readBy[currentUserUid]));
    }

    componentDidUpdate(prevProps) {
        // Text can be updated from outside (by a change in Firebase), so keep state in sync
        const { text } = this.props;
        if (prevProps.text !== text) {
            this.setState({ text });
        }
    }

    getLikerNames(likesMap) {
        const { resolveUserName } = this.props;
        if (!likesMap || !resolveUserName) {
            return [];
        }
        return Object.keys(likesMap).map(uid => resolveUserName(uid)).filter(Boolean);
    }

    getLikeTooltip(likesMap) {
        return getLikeTooltipTitle(this.getLikerNames(likesMap));
    }

    renderLikeButton({ active, count, likesMap, onClick, testId = "like-comment" }) {
        return (
            <Tooltip title={this.getLikeTooltip(likesMap)} enterDelay={700} arrow>
                <LikeButton
                    type="button"
                    data-test-id={testId}
                    $active={active}
                    $hasCount={count > 0}
                    aria-pressed={active}
                    aria-label="Like"
                    onMouseDown={event => {
                        event.preventDefault();
                        event.stopPropagation();
                    }}
                    onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                        onClick();
                    }}
                >
                    <Icon style={{ fontSize: 14 }}>thumb_up_off_alt</Icon>
                    {count > 0 && <span>{count}</span>}
                </LikeButton>
            </Tooltip>
        );
    }

    showReplyLimitMessage() {
        ShowErrorDialog({
            title: "You've reached the limit for this thread.",
            message: "Delete a reply to continue here, or start a new comment thread."
        });
    }

    getReplyMenuItems(reply) {
        const { currentUserUid, canEditPresentation, deleteReply } = this.props;
        const isMyReply = reply.authorUid === currentUserUid;

        return [
            {
                condition: isMyReply,
                text: "Edit Reply",
                onClick: () => this.setState({ editingReplyId: reply.id }),
                testId: "edit-reply-menu-item"
            },
            {
                condition: isMyReply || canEditPresentation,
                text: "Delete Reply",
                onClick: () => deleteReply && deleteReply(reply.id),
                testId: "delete-reply-menu-item"
            }
        ].filter(item => item.condition);
    }

    renderReply(reply, index = 0) {
        const {
            byViewer,
            surface,
            canEdit,
            currentSlide,
            presentation,
            collaborators,
            teammates,
            currentUserUid,
            updateReply,
            toggleReplyLike,
            resolved
        } = this.props;
        const { editingReplyId } = this.state;

        const author = reply.author || {};
        const isMyReply = reply.authorUid === currentUserUid;
        const replyNameLabel = `${author.displayName || author.email}${isMyReply ? " (You)" : ""}`;
        const replyLikeCount = reply.likes ? Object.keys(reply.likes).length : 0;
        const replyLikedByMe = !!(reply.likes && currentUserUid && reply.likes[currentUserUid]);

        if (editingReplyId === reply.id) {
            return (
                <ReplyEditWrap key={reply.id}>
                    <CommentComposer
                        autoFocus
                        submitVariant="buttons"
                        submitLabel="Update"
                        placeholder={byViewer ? REPLY_PLACEHOLDER_VIEWER : REPLY_PLACEHOLDER}
                        initialText={reply.text}
                        byViewer={byViewer}
                        surface={surface}
                        collaborators={collaborators}
                        teammates={teammates}
                        presentation={presentation}
                        currentSlide={currentSlide}
                        canEdit={canEdit}
                        onSubmit={async (t, taggedUsers, pendingTaggedUsers) => {
                            await (updateReply && updateReply(reply.id, t, taggedUsers, pendingTaggedUsers));
                            this.setState({ editingReplyId: null });
                        }}
                        onCancel={() => this.setState({ editingReplyId: null })}
                    />
                </ReplyEditWrap>
            );
        }

        const replyMenuItems = this.getReplyMenuItems(reply);

        return (
            <ReplyBlock key={reply.id} className={classNames({ "comment-reply": true, withDivider: index > 0 })}>
                <CardTop>
                    <PostedAvatar src={author.photoURL} alt={author.displayName || author.email}>
                        {!author.photoURL && <Icon>person</Icon>}
                    </PostedAvatar>
                    <HeaderText>
                        <AuthorNameWithTooltip name={replyNameLabel} />
                        <SubRow>
                            <span>{moment(reply.createdAt).fromNow()}</span>
                        </SubRow>
                    </HeaderText>
                    <TopActions>
                        {
                            this.renderLikeButton({
                                active: replyLikedByMe,
                                count: replyLikeCount,
                                likesMap: reply.likes,
                                onClick: () => toggleReplyLike && toggleReplyLike(reply.id, !replyLikedByMe),
                                testId: "like-reply"
                            })
                        }
                        {
                            !resolved && replyMenuItems.length > 0 &&
                            <KebabIcon data-test-id="reply-menu" onClick={event => this.setState({ replyKebabAnchorEl: event.currentTarget, activeReplyId: reply.id })}>
                                more_vert
                            </KebabIcon>
                        }
                    </TopActions>
                </CardTop>
                <CommentBodyWrap>
                    <EditContentEditable
                        className="commentText"
                        $isViewer={byViewer}
                        html={reply.text}
                        disabled={true}
                    />
                </CommentBodyWrap>
            </ReplyBlock>
        );
    }

    render() {
        const {
            isNew,
            isMy,
            canEditPresentation,
            byViewer,
            surface,
            slideIndex,
            author,
            createdAt,
            goToSlide,
            canEdit,
            currentSlide,
            presentation,
            collaborators,
            teammates,
            assignedUser,
            likes,
            likeCount,
            likedByMe,
            toggleLike,
            resolve,
            resolved,
            remove,
            createReply,
            post,
            update,
            replies = []
        } = this.props;
        const {
            text,
            isEditing,
            isReplying,
            kebabAnchorEl,
            repliesCollapsed,
            replyKebabAnchorEl,
            activeReplyId
        } = this.state;

        const newCommentPlaceholder = byViewer ? NEW_COMMENT_PLACEHOLDER_VIEWER : NEW_COMMENT_PLACEHOLDER;
        const replyPlaceholder = byViewer ? REPLY_PLACEHOLDER_VIEWER : REPLY_PLACEHOLDER;

        // New comment composer
        if (isNew) {
            return (
                <Container className={classNames({ comment: true, isNew: true })}>
                    <CommentComposer
                        autoFocus
                        showAssignToSlide
                        clearAfterSubmit
                        submitVariant="icon"
                        placeholder={newCommentPlaceholder}
                        initialText=""
                        byViewer={byViewer}
                        surface={surface}
                        collaborators={collaborators}
                        teammates={teammates}
                        presentation={presentation}
                        currentSlide={currentSlide}
                        canEdit={canEdit}
                        assignedUser={assignedUser}
                        onSubmit={(t, taggedUsers, pendingTaggedUsers) => post(t, taggedUsers, pendingTaggedUsers)}
                    />
                </Container>
            );
        }

        // Editing an existing comment
        if (isEditing) {
            return (
                <Container className={classNames({ comment: true, isEditing: true })}>
                    <CommentComposer
                        autoFocus
                        submitVariant="buttons"
                        submitLabel="Update"
                        placeholder={newCommentPlaceholder}
                        initialText={text}
                        byViewer={byViewer}
                        surface={surface}
                        collaborators={collaborators}
                        teammates={teammates}
                        presentation={presentation}
                        currentSlide={currentSlide}
                        canEdit={canEdit}
                        onSubmit={async (t, taggedUsers, pendingTaggedUsers) => {
                            await update(t, taggedUsers, pendingTaggedUsers);
                            this.setState({ isEditing: false });
                        }}
                        onCancel={() => this.setState({ isEditing: false })}
                    />
                </Container>
            );
        }

        // Read-only posted comment card
        const readonlyMenuItems = resolved
            ? [
                {
                    condition: true,
                    text: "Restore Comment",
                    onClick: () => resolve && resolve(false),
                    testId: "restore-comment-menu-item"
                },
                {
                    // Match the backend deleteComment rule (author or presentation editor);
                    // otherwise the option shows but the request is rejected and fails silently
                    condition: isMy || canEditPresentation,
                    text: "Delete Comment",
                    onClick: () => remove && remove(),
                    testId: "delete-comment-menu-item"
                }
            ].filter(item => item.condition)
            : [
                {
                    condition: isMy,
                    text: "Edit Comment",
                    onClick: () => this.setState({ isEditing: true }),
                    testId: "edit-comment-menu-item"
                },
                {
                    condition: true,
                    text: "Resolve comment",
                    onClick: () => resolve && resolve(true),
                    testId: "resolve-comment-menu-item"
                }
            ].filter(item => item.condition);

        const activeReply = replies.find(reply => reply.id === activeReplyId);
        const replyLimitReached = replies.length >= MAX_COMMENT_REPLIES;

        return (
            <Container className={classNames({ comment: true, isReadonly: true })}>
                <PostedCard>
                    <CardTop>
                        <PostedAvatar
                            src={author.photoURL}
                            alt={author.displayName || author.email}
                        >
                            {!author.photoURL && <Icon>person</Icon>}
                        </PostedAvatar>
                        <HeaderText>
                            <AuthorNameWithTooltip name={`${author.displayName || author.email}${isMy ? " (You)" : ""}`} />
                            <SubRow>
                                <span>{moment(createdAt).fromNow()}</span>
                                <SubDivider>|</SubDivider>
                                <SlideLink onClick={() => goToSlide && goToSlide(slideIndex)}>
                                    Slide {slideIndex + 1}
                                </SlideLink>
                            </SubRow>
                        </HeaderText>
                        <TopActions>
                            {
                                this.renderLikeButton({
                                    active: likedByMe,
                                    count: likeCount,
                                    likesMap: likes,
                                    onClick: () => toggleLike && toggleLike(!likedByMe)
                                })
                            }
                            {
                                readonlyMenuItems.length > 0 &&
                                <KebabIcon data-test-id="comment-menu" onClick={event => this.setState({ kebabAnchorEl: event.currentTarget })}>
                                    more_vert
                                </KebabIcon>
                            }
                        </TopActions>
                    </CardTop>
                    <CommentBodyWrap>
                        <EditContentEditable
                            className="commentText"
                            $isViewer={byViewer}
                            html={text}
                            disabled={true}
                        />
                    </CommentBodyWrap>
                    {
                        replies.length > 0 &&
                        <>
                            <ThreadDivider>
                                <ThreadToggle data-test-id="toggle-replies" onClick={() => this.setState({ repliesCollapsed: !repliesCollapsed })}>
                                    {replies.length} {replies.length === 1 ? "REPLY" : "REPLIES"}
                                    <Icon style={{ fontSize: 20 }}>{repliesCollapsed ? "arrow_drop_up" : "arrow_drop_down"}</Icon>
                                </ThreadToggle>
                                <ThreadLine />
                            </ThreadDivider>
                            {!repliesCollapsed && replies.map((reply, index) => this.renderReply(reply, index))}
                        </>
                    }
                    {
                        !resolved && !repliesCollapsed && (
                            (isReplying && !replyLimitReached) ? (
                                <ReplyComposerWrap>
                                    <CommentComposer
                                        autoFocus
                                        submitVariant="icon"
                                        placeholder={replyPlaceholder}
                                        initialText=""
                                        byViewer={byViewer}
                                        surface={surface}
                                        collaborators={collaborators}
                                        teammates={teammates}
                                        presentation={presentation}
                                        currentSlide={currentSlide}
                                        canEdit={canEdit}
                                        onSubmit={async (t, taggedUsers, pendingTaggedUsers) => {
                                            await (createReply && createReply(t, taggedUsers, pendingTaggedUsers));
                                            this.setState({ isReplying: false });
                                        }}
                                        onEmptyBlur={() => this.setState({ isReplying: false })}
                                    />
                                </ReplyComposerWrap>
                            ) : (
                                <>
                                    <CardDivider />
                                    <FooterRow>
                                        <Tooltip title="Resolve" enterDelay={700} arrow>
                                            <FooterIcon data-test-id="resolve-comment" onClick={() => resolve && resolve(true)}>
                                                check_circle_outline
                                            </FooterIcon>
                                        </Tooltip>
                                        <Tooltip title={replyLimitReached ? "Reply limit reached" : "Reply"} enterDelay={700} arrow>
                                            <FooterIcon
                                                data-test-id="reply-comment"
                                                className={classNames({ disabled: replyLimitReached })}
                                                onClick={() => replyLimitReached ? this.showReplyLimitMessage() : this.setState({ isReplying: true })}
                                            >
                                                reply
                                            </FooterIcon>
                                        </Tooltip>
                                    </FooterRow>
                                </>
                            )
                        )
                    }
                    {/* Render the action menus under the base app theme (not the pane's
                        dialogTheme) so their hover, icon spacing and typography match every
                        other kebab menu in the app. */}
                    <MuiThemeProvider theme={muiTheme}>
                        <Menu
                            anchorEl={kebabAnchorEl}
                            open={!!kebabAnchorEl}
                            onClose={() => this.setState({ kebabAnchorEl: null })}
                            getContentAnchorEl={null}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                            {
                                readonlyMenuItems.map(item => (
                                    <MenuItem
                                        key={item.text}
                                        data-test-id={item.testId}
                                        onClick={() => {
                                            item.onClick();
                                            this.setState({ kebabAnchorEl: null });
                                        }}
                                    >
                                        {item.text}
                                    </MenuItem>
                                ))
                            }
                        </Menu>
                    </MuiThemeProvider>
                    <MuiThemeProvider theme={muiTheme}>
                        <Menu
                            anchorEl={replyKebabAnchorEl}
                            open={!!replyKebabAnchorEl}
                            onClose={() => this.setState({ replyKebabAnchorEl: null, activeReplyId: null })}
                            getContentAnchorEl={null}
                            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                            transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                            {
                                (activeReply ? this.getReplyMenuItems(activeReply) : []).map(item => (
                                    <MenuItem
                                        key={item.text}
                                        data-test-id={item.testId}
                                        onClick={() => {
                                            item.onClick();
                                            this.setState({ replyKebabAnchorEl: null, activeReplyId: null });
                                        }}
                                    >
                                        {item.text}
                                    </MenuItem>
                                ))
                            }
                        </Menu>
                    </MuiThemeProvider>
                </PostedCard>
            </Container>
        );
    }
}

export default Comment;
